export const BULK_PASSENGER_MAX = 10;

/** Direcciones ficticias de importación o alta temporal; no son domicilios reales. */
const PLACEHOLDER_ADDRESS_PATTERNS = [
  /^pendiente\s+importaci[oó]n$/i,
  /^por\s+registrar$/i,
  /^sin\s+direcci[oó]n$/i,
  /^pendiente$/i,
];

export function normalizeStreet(value: string): string {
  return (value || '').trim().replace(/\s+/g, ' ');
}

export function isPlaceholderAddress(value: string): boolean {
  const street = normalizeStreet(value).toLowerCase();
  if (!street) return true;
  return PLACEHOLDER_ADDRESS_PATTERNS.some((pattern) => pattern.test(street));
}

export type BulkPassengerRow = {
  name: string;
  first_lastname: string;
  second_lastname: string;
  phone: string;
  street: string;
};

export type BulkPassengerRowErrors = Partial<
  Record<keyof BulkPassengerRow | '_row', string>
>;

export type ExistingPassengerLike = {
  name?: string;
  first_lastname?: string;
  last_name?: string;
};

export function personKey(name: string, firstLastname: string): string {
  return `${(name || '').trim().toLowerCase()}|${(firstLastname || '').trim().toLowerCase()}`;
}

export function existingPersonKey(p: ExistingPassengerLike): string {
  const rawName = (p.name || '').trim();
  const firstFromField = (p.first_lastname || p.last_name || '').trim();

  if (rawName.includes(' ') && !firstFromField) {
    const parts = rawName.split(/\s+/);
    const first = parts[0] ?? '';
    const last = parts.slice(1).join(' ');
    return personKey(first, last);
  }

  return personKey(rawName, firstFromField);
}

export function validateBulkRow(row: BulkPassengerRow): BulkPassengerRowErrors {
  const errors: BulkPassengerRowErrors = {};
  if (!(row.name || '').trim()) {
    errors.name = 'Requerido';
  }
  if (!(row.first_lastname || '').trim()) {
    errors.first_lastname = 'Requerido';
  }
  const street = normalizeStreet(row.street);
  if (!street) {
    errors.street = 'Dirección requerida';
  } else if (isPlaceholderAddress(street)) {
    errors.street = 'Ingresa la dirección real del pasajero';
  }
  return errors;
}

export function pickPreferredAddress(
  addresses: { id: number; street: string }[] | undefined,
  preferredStreet?: string
): { id: number; street: string } | null {
  if (!addresses?.length) return null;

  const preferred = normalizeStreet(preferredStreet ?? '');
  if (preferred) {
    const exact = addresses.find(
      (a) => normalizeStreet(a.street).toLowerCase() === preferred.toLowerCase()
    );
    if (exact && !isPlaceholderAddress(exact.street)) {
      return exact;
    }
  }

  const real = [...addresses]
    .filter((a) => !isPlaceholderAddress(a.street))
    .sort((a, b) => b.id - a.id);
  if (real.length > 0) return real[0];

  if (preferred && !isPlaceholderAddress(preferred)) {
    return { id: 0, street: preferred };
  }

  return null;
}

export function findIntraBatchDuplicateIndexes(rows: BulkPassengerRow[]): Set<number> {
  const seen = new Map<string, number>();
  const dupes = new Set<number>();

  rows.forEach((row, index) => {
    const key = personKey(row.name, row.first_lastname);
    if (!key.replace('|', '').trim()) return;
    const prev = seen.get(key);
    if (prev !== undefined) {
      dupes.add(prev);
      dupes.add(index);
    } else {
      seen.set(key, index);
    }
  });

  return dupes;
}

export function findExistingDuplicateIndexes(
  rows: BulkPassengerRow[],
  existing: ExistingPassengerLike[]
): Set<number> {
  const existingKeys = new Set(existing.map(existingPersonKey));
  const dupes = new Set<number>();

  rows.forEach((row, index) => {
    const key = personKey(row.name, row.first_lastname);
    if (key.replace('|', '').trim() && existingKeys.has(key)) {
      dupes.add(index);
    }
  });

  return dupes;
}

export function validateBulkRows(
  rows: BulkPassengerRow[],
  existing: ExistingPassengerLike[] = []
): BulkPassengerRowErrors[] {
  const intraDupes = findIntraBatchDuplicateIndexes(rows);
  const existingDupes = findExistingDuplicateIndexes(rows, existing);

  return rows.map((row, index) => {
    const errors = validateBulkRow(row);
    if (intraDupes.has(index)) {
      errors._row = 'Duplicado en esta lista (mismo nombre y primer apellido)';
    } else if (existingDupes.has(index)) {
      errors._row = 'Ya existe un pasajero con ese nombre y apellido en la empresa';
    }
    return errors;
  });
}
